import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const GameSettingsPopup = ({
  isOpen,
  onClose,
  gameMetadata,
  boardSize,
  puzzleState,
  setPuzzleState,
  gameType: incomingGameType, // Accept game type from parent
  manualOpen = false, // New prop to indicate if opened manually
  startingColor = 'w', // Add prop for starting color of the puzzle
  onSaveForLater = null, // New prop to handle bookmark action
  onNextPuzzle = null, // New prop to handle next puzzle navigation
  isInPuzzleFilter = false, // New prop to check if we're in a puzzle filter
  redirectToPortal = null, // New prop to redirect to portal with puzzle filter
  onProveMeWrongClick = null // Add this prop
}) => {
  // Use incoming game type if provided, otherwise detect it
  const [gameType, setGameType] = useState(incomingGameType || 'default');
  const [copiedFeedback, setCopiedFeedback] = useState(false);
  // Add state to track if we're in a puzzle filter from URL
  const [isInPuzzleFilterFromUrl, setIsInPuzzleFilterFromUrl] = useState(false);

  // Check URL for puzzle filter when component mounts
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlTag = urlParams.get('tag');
    
    // Check for puzzle tag with case insensitivity
    if (urlTag && urlTag.toUpperCase() === 'PUZZLE') {
      setIsInPuzzleFilterFromUrl(true);
    }
  }, []);

  // Format puzzle solution in SAN notation with proper move numbering
  const formatPuzzleSolution = () => {
    if (!gameMetadata?.moves || !Array.isArray(gameMetadata.moves)) {
      return 'No solution available';
    }

    let formattedSolution = '';
    let moveNumber = 1;
    let isWhiteToMove = startingColor === 'b'; // Not sure why, but this is the correct way to format the solution (seems opposite)

    // Handle games where black moves first
    if (!isWhiteToMove) {
      formattedSolution = '1... ';
    }

    gameMetadata.moves.forEach((move, index) => {
      if (!move || !move.notation) return;

      // For white's move, add the move number first
      if (isWhiteToMove) {
        formattedSolution += `${moveNumber}. ${move.notation} `;
      } else {
        // For black's move, just add the notation and then increment the move number
        formattedSolution += `${move.notation} `;
        moveNumber++;
      }

      // Toggle the side to move
      isWhiteToMove = !isWhiteToMove;
    });

    return formattedSolution.trim();
  };

  // Handle copying solution to clipboard
  const copyPuzzleSolution = () => {
    const solution = formatPuzzleSolution();
    navigator.clipboard.writeText(solution)
      .then(() => {
        setCopiedFeedback(true);
        setTimeout(() => setCopiedFeedback(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy solution:', err);
      });
  };

  // Reference to the popup content
  const popupContentRef = useRef(null);

  // Handle click outside the popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupContentRef.current && !popupContentRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add the event listener when the popup is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener when the component unmounts or the popup closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // For challenges
  const [engineDifficulty, setEngineDifficulty] = useState(() => {
    return parseInt(localStorage.getItem('engineDifficulty') || '5');
  });

  // For default games
  const [gameMode, setGameMode] = useState(() => {
    return localStorage.getItem('gameMode') || 'manual';
  });
  const [showUndoButton, setShowUndoButton] = useState(() => {
    return localStorage.getItem('showUndoButton') !== 'false';
  });

  // Update game type when incomingGameType changes
  useEffect(() => {
    if (incomingGameType) {
      setGameType(incomingGameType);
    }
  }, [incomingGameType]);

  // If not receiving game type from parent, detect it from metadata
  useEffect(() => {
    if (!incomingGameType && isOpen) {


      // Check for tags in different possible formats
      let hasPuzzleTag = false;
      let hasChallengeTag = false;

      // Check if tags exist as an array
      if (Array.isArray(gameMetadata?.tags)) {
        hasPuzzleTag = gameMetadata.tags.some(tag =>
          typeof tag === 'string' && tag.toUpperCase() === 'PUZZLE'
        );
        hasChallengeTag = gameMetadata.tags.some(tag =>
          typeof tag === 'string' && tag.toUpperCase() === 'CHALLENGE'
        );
      }

      // Check if gameMetadata.tag exists (singular)
      if (!hasPuzzleTag && !hasChallengeTag && typeof gameMetadata?.tag === 'string') {
        hasPuzzleTag = gameMetadata.tag.toUpperCase() === 'PUZZLE';
        hasChallengeTag = gameMetadata.tag.toUpperCase() === 'CHALLENGE';
      }

      // Check gameId for tag identifiers
      if (!hasPuzzleTag && !hasChallengeTag && gameMetadata?.id) {
        hasPuzzleTag = gameMetadata.id.toUpperCase().includes('PUZZLE');
        hasChallengeTag = gameMetadata.id.toUpperCase().includes('CHALLENGE');
      }

      // Additional check - look for challenge/puzzle in title
      if (!hasPuzzleTag && !hasChallengeTag && gameMetadata?.title) {
        hasPuzzleTag = gameMetadata.title.toUpperCase().includes('PUZZLE');
        hasChallengeTag = gameMetadata.title.toUpperCase().includes('CHALLENGE');
      }

      // Check URL for query parameters indicating challenge/puzzle
      const urlParams = new URLSearchParams(window.location.search);
      const urlTag = urlParams.get('tag');
      if (!hasPuzzleTag && !hasChallengeTag && urlTag) {
        hasPuzzleTag = urlTag.toUpperCase() === 'PUZZLE';
        hasChallengeTag = urlTag.toUpperCase() === 'CHALLENGE';
      }

      // Set the game type based on our checks
      if (hasPuzzleTag) {
        setGameType('puzzle');
      } else if (hasChallengeTag) {
        setGameType('challenge');
      } else {
        setGameType('default');
      }


    }
  }, [gameMetadata, isOpen, gameType, incomingGameType]);

  // Handle difficulty change
  const handleDifficultyChange = (delta) => {
    setEngineDifficulty(prev => {
      const newValue = Math.max(1, Math.min(10, prev + delta));
      const prevValue = localStorage.getItem('engineDifficulty');

      // Save to localStorage
      localStorage.setItem('engineDifficulty', newValue.toString());

      // Dispatch a custom event to notify ViewGame component about difficulty change
      // This is more reliable than the storage event which may not fire within the same window
      if (prevValue !== newValue.toString()) {

        const event = new CustomEvent('engineDifficultyChanged', {
          detail: { engineDifficulty: newValue }
        });
        document.dispatchEvent(event);
      }

      return newValue;
    });
  };

  // Handle try again for puzzles - close and reset
  const handleTryAgain = () => {
    if (setPuzzleState) {
      setPuzzleState('trying'); // Set to a temporary state to indicate user is trying again
    }
    onClose();
  };

  // Handle start over - completely reset the puzzle
  const handleStartOver = () => {
    if (setPuzzleState) {
      setPuzzleState('reset'); // New state to completely reset the puzzle
    }
    onClose();
  };

  // Handle show solution for puzzles
  const handleShowSolution = () => {
    // Updated to auto-play all moves
    if (setPuzzleState) {
      setPuzzleState('showAll');
    }
    onClose();
  };

  // Handle prove me wrong - start computer play
  const handleProveMeWrong = () => {
    if (onProveMeWrongClick) {
      onProveMeWrongClick();
    }
    onClose();
  };

  // Handle next puzzle or redirect to portal
  const handleNextPuzzle = () => {
    // Set puzzle state to completed
    if (setPuzzleState) {
      setPuzzleState('completed');
    }

    // Close the popup
    onClose();

    // If in puzzle filter and onNextPuzzle is provided, navigate to next puzzle
    if ((isInPuzzleFilter || isInPuzzleFilterFromUrl) && onNextPuzzle) {
      onNextPuzzle();
    }
    // If not in puzzle filter and redirectToPortal is provided, redirect to portal
    else if (!(isInPuzzleFilter || isInPuzzleFilterFromUrl) && redirectToPortal) {
      redirectToPortal();
    }
  };

  // Handle saving settings
  const handleSaveSettings = () => {
    // Save settings based on game type
    if (gameType === 'challenge') {
      localStorage.setItem('engineDifficulty', engineDifficulty.toString());
      // In the future, this would trigger stockfish setup

    } else if (gameType === 'default') {
      // Store previous gameMode before changing
      const prevGameMode = localStorage.getItem('gameMode');

      // Save new settings to localStorage
      localStorage.setItem('gameMode', gameMode);
      localStorage.setItem('engineDifficulty', engineDifficulty.toString());
      localStorage.setItem('showUndoButton', showUndoButton.toString());



      // Dispatch a custom event to notify ViewGame component about gameMode change
      // This is more reliable than the storage event which may not fire within the same window
      if (prevGameMode !== gameMode) {

        const event = new CustomEvent('gameModeChanged', {
          detail: { gameMode }
        });
        document.dispatchEvent(event);
      }
    }

    onClose();
  };

  if (!isOpen) return null;

  // Base styles for all buttons
  const buttonBaseStyle = {
    background: 'rgba(30, 30, 30, 0.8)',
    color: 'white',
    border: '1px solid rgba(255, 215, 0, 0.5)',
    padding: '12px 15px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
  };

  // Styles for different button types
  const buttonStyles = {
    puzzle: {
      borderLeft: '4px solid rgb(0, 120, 212)',
    },
    challenge: {
      borderLeft: '4px solid rgb(255, 0, 80)',
    },
    default: {
      borderLeft: '4px solid rgb(50, 180, 50)',
    },
    success: {
      background: 'rgba(50, 180, 50, 0.3)',
      borderLeft: '4px solid rgb(50, 180, 50)',
    },
    warning: {
      background: 'rgba(255, 180, 0, 0.3)',
      borderLeft: '4px solid rgb(255, 180, 0)',
    },
    danger: {
      background: 'rgba(255, 50, 50, 0.3)',
      borderLeft: '4px solid rgb(255, 50, 50)',
    }
  };

  // Render puzzle content
  const renderPuzzleContent = () => {
    // Use passed puzzleState if available, otherwise use internal state
    const currentPuzzleState = puzzleState || 'incorrect';

    // If manually opened (gear icon click), show different options
    if (manualOpen && gameType === 'puzzle') {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '15px',
        }}>

          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.warning,
            }}
            onClick={handleStartOver}
          >
            <i className="fa-solid fa-arrow-rotate-left" style={{ marginRight: '10px' }}></i>
            Start Over
          </button>

          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.puzzle,
            }}
            onClick={handleShowSolution}
          >
            <i className="fa-solid fa-lightbulb" style={{ marginRight: '10px' }}></i>
            Show Solution
          </button>
        </div>
      );
    }

    // For successful completion
    if (currentPuzzleState === 'completed') {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '15px',
        }}>
          <div style={{
            background: 'rgba(50, 180, 50, 0.2)',
            padding: '15px',
            borderRadius: '5px',
            textAlign: 'center',
            marginBottom: '10px',
            border: '1px solid rgba(50, 180, 50, 0.5)',
          }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'rgb(50, 180, 50)' }}>
              PUZZLE SOLVED!
            </span>
          </div>

          {/* Puzzle Solution Section */}
          <div style={{
            background: 'rgba(255, 215, 0, 0.1)',
            padding: '15px',
            borderRadius: '5px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            marginBottom: '10px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'gold' }}>
                Puzzle Solution:
              </span>
              <button
                onClick={copyPuzzleSolution}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255, 215, 0, 0.5)',
                  color: copiedFeedback ? 'rgb(50, 180, 50)' : 'gold',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  transition: 'all 0.2s ease',
                }}
              >
                <i className={`fa-solid ${copiedFeedback ? 'fa-check' : 'fa-copy'}`}></i>
                {copiedFeedback ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '10px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '15px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '100px',
              overflowY: 'auto',
              color: '#fff',
            }}>
              {formatPuzzleSolution()}
            </div>
          </div>



          {/* <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.warning,
            }}
            onClick={handleStartOver}
          >
            <i className="fa-solid fa-arrow-rotate-left" style={{ marginRight: '10px' }}></i>
            Start Over
          </button> */}

          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.puzzle,
              borderLeft: '4px solid gold',
            }}
            onClick={() => {
              // If onSaveForLater function is provided, call it before closing
              if (onSaveForLater) {
                onSaveForLater();
              }
              onClose();
            }}
          >
            <i className="fa-solid fa-bookmark" style={{ marginRight: '10px', color: 'gold' }}></i>
            Save For Later
          </button>
          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.success,
            }}
            onClick={handleNextPuzzle}
          >
            <i className="fa-solid fa-arrow-right" style={{ marginRight: '10px' }}></i>
            {isInPuzzleFilter || isInPuzzleFilterFromUrl ? "Next Puzzle" : "Show More Puzzles"}
          </button>
        </div>
      );
    } else {
      // Incorrect puzzle state
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: '15px',
        }}>
          <div style={{
            background: 'rgba(255, 50, 50, 0.2)',
            padding: '15px',
            borderRadius: '5px',
            textAlign: 'center',
            marginBottom: '10px',
            border: '1px solid rgba(255, 50, 50, 0.5)',
          }}>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'rgb(255, 50, 50)' }}>
              Incorrect!
            </span>
          </div>
          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.puzzle,
            }}
            onClick={handleShowSolution}
          >
            <i className="fa-solid fa-lightbulb" style={{ marginRight: '10px' }}></i>
            Show Solution
          </button>
          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.warning,
            }}
            onClick={handleTryAgain}
          >
            <i className="fa-solid fa-rotate-left" style={{ marginRight: '10px' }}></i>
            Try Again
          </button>

          

          {/* Add solution preview for immediate reference */}
          {/* <div style={{
            background: 'rgba(255, 215, 0, 0.1)',
            padding: '15px',
            borderRadius: '5px',
            border: '1px solid rgba(255, 215, 0, 0.3)',
            marginBottom: '10px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}>
              <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'gold' }}>
                Puzzle Solution:
              </span>
              <button
                onClick={copyPuzzleSolution}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255, 215, 0, 0.5)',
                  color: copiedFeedback ? 'rgb(50, 180, 50)' : 'gold',
                  padding: '5px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  transition: 'all 0.2s ease',
                }}
              >
                <i className={`fa-solid ${copiedFeedback ? 'fa-check' : 'fa-copy'}`}></i>
                {copiedFeedback ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '10px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '15px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '100px',
              overflowY: 'auto',
              color: '#fff',
            }}>
              {formatPuzzleSolution()}
            </div>
          </div> */}
{/* 
          <button
            className="settings-button"
            style={{
              ...buttonBaseStyle,
              ...buttonStyles.success,
            }}
            onClick={handleProveMeWrong}
          >
            <i className="fa-solid fa-robot" style={{ marginRight: '10px' }}></i>
            "Prove Me Wrong" (Play vs Computer)
          </button> */}
        </div>
      );
    }
  };

  // Render challenge content
  const renderChallengeContent = () => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '15px',
      }}>
        <div style={{
          background: 'rgba(255 0, 80, 0.2)',
          padding: '15px',
          borderRadius: '5px',
          textAlign: 'center',
          marginBottom: '10px',
          border: '1px solid rgba(255, 0, 80, 0.5)',
        }}>
          <span style={{ fontSize: '16px', color: 'white' }}>
            Select engine difficulty to play against.
          </span>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(30, 30, 30, 0.6)',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '10px',
        }}>
          <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Engine Difficulty:</span>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => handleDifficultyChange(-1)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 10px',
              }}
            >
              <i className="fa-solid fa-minus"></i>
            </button>

            <span style={{
              fontSize: '20px',
              fontWeight: 'bold',
              padding: '5px 15px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '5px',
              minWidth: '30px',
              textAlign: 'center',
            }}>
              {engineDifficulty}
            </span>

            <button
              onClick={() => handleDifficultyChange(1)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 10px',
              }}
            >
              <i className="fa-solid fa-plus"></i>
            </button>
          </div>
        </div>

        <button
          className="settings-button"
          style={{
            ...buttonBaseStyle,
            ...buttonStyles.challenge,
            justifyContent: 'center',
            fontSize: '18px',
            padding: '15px',
          }}
          onClick={handleSaveSettings}
        >
          <i className="fa-solid fa-chess-knight" style={{ marginRight: '10px' }}></i>
          Start Playing
        </button>
      </div>
    );
  };

  // Render default game content
  const renderDefaultContent = () => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '15px',
      }}>
        <div style={{ marginBottom: '10px' }}>
          <div style={{
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '10px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            paddingBottom: '5px',
          }}>
            Opponent Moves Responses:
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>



            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '10px',
              background: gameMode === 'manual' ? 'rgba(50, 180, 50, 0.2)' : 'transparent',
              borderRadius: '5px',
              transition: 'background 0.2s ease',
            }}>
              <input
                type="radio"
                name="gameMode"
                value="manual"
                checked={gameMode === 'manual'}
                onChange={() => setGameMode('manual')}
                style={{ marginRight: '10px' }}
              />
              I Want To Control Both Sides
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '10px',
              background: gameMode === 'exploration' ? 'rgba(50, 180, 50, 0.2)' : 'transparent',
              borderRadius: '5px',
              transition: 'background 0.2s ease',
            }}>
              <input
                type="radio"
                name="gameMode"
                value="exploration"
                checked={gameMode === 'exploration'}
                onChange={() => setGameMode('exploration')}
                style={{ marginRight: '10px' }}
              />
              I Want Stockfish To Control My Opponent's Side
            </label>
          </div>
        </div>

        {gameMode !== 'manual' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(30, 30, 30, 0.6)',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '10px',
          }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Engine Difficulty:</span>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => handleDifficultyChange(-1)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0 10px',
                }}
              >
                <i className="fa-solid fa-minus"></i>
              </button>

              <span style={{
                fontSize: '20px',
                fontWeight: 'bold',
                padding: '5px 15px',
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '5px',
                minWidth: '30px',
                textAlign: 'center',
              }}>
                {engineDifficulty}
              </span>

              <button
                onClick={() => handleDifficultyChange(1)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0 10px',
                }}
              >
                <i className="fa-solid fa-plus"></i>
              </button>
            </div>
          </div>
        )}



        <button
          className="settings-button"
          style={{
            ...buttonBaseStyle,
            ...buttonStyles.default,
            justifyContent: 'center',
            fontSize: '18px',
            padding: '15px',
          }}
          onClick={handleSaveSettings}
        >
          <i className="fa-solid fa-check" style={{ marginRight: '10px' }}></i>
          Apply Settings
        </button>
      </div>
    );
  };

  // Render content based on game type
  const renderContentBasedOnGameType = () => {
    switch (gameType) {
      case 'puzzle':
        return renderPuzzleContent();
      case 'challenge':
        return renderChallengeContent();
      default:
        return renderDefaultContent();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div
        ref={popupContentRef}
        style={{
          position: 'relative',
          background: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '20px',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)',
          border: '2px solid gold',
          minWidth: '350px',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          animation: 'fadeIn 0.3s ease-out',
        }}
      >
        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .settings-button:hover {
              background: rgba(50, 50, 50, 0.9) !important;
              border-color: gold !important;
              transform: translateY(-2px) !important;
              box-Shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;
            }
            
            input[type="radio"]:focus, input[type="checkbox"]:focus {
              outline: 1px solid gold;
            }
          `}
        </style>
        <div style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid gold',
          paddingBottom: '10px',
          marginBottom: '5px'
        }}>
          <h3 style={{ margin: 0, color: 'gold' }}>
            {gameType === 'puzzle' ? 'Puzzle Settings' :
              gameType === 'challenge' ? 'Challenge Settings' : 'Game Settings'}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer'
            }}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {renderContentBasedOnGameType()}
      </div>
    </div>
  );
};

export default GameSettingsPopup; 